import { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { COUNTRIES, PROPERTY_TYPES, INVESTOR_STATUS, CURRENCIES } from "@/lib/constants";
import CountryCombobox from "@/components/CountryCombobox";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";

const EMPTY = {
  name: "", email: "", phone: "", country: "France", currency: "EUR",
  budget_min: "", budget_max: "", types: [], zones: [],
  status: "Prospect", nda_signed: false,
};

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-slate-700">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Chips({ options, selected, onToggle, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => {
        const active = selected.includes(o);
        return (
          <button
            key={o}
            type="button"
            onClick={() => onToggle(o)}
            data-testid={`${testid}-${o}`}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
              active
                ? "border-[#0F172A] bg-[#0F172A] text-white"
                : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
            }`}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}

export default function InvestorForm({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(initial ? { ...EMPTY, ...initial } : EMPTY);
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, val) =>
    setForm((f) => ({ ...f, [k]: f[k].includes(val) ? f[k].filter((x) => x !== val) : [...f[k], val] }));

  const submit = async () => {
    if (!form.name) {
      toast.error("Le nom/société est obligatoire");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      budget_min: Number(form.budget_min) || 0,
      budget_max: Number(form.budget_max) || 0,
    };
    try {
      if (initial?.id) {
        await api.put(`/investors/${initial.id}`, payload);
        toast.success("Investisseur modifié");
      } else {
        await api.post("/investors", payload);
        toast.success("Investisseur ajouté");
      }
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
          onClick={() => onOpenChange(false)}
          data-testid="investor-form-backdrop"
        />
      )}
      <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
        <DialogContent
          className="max-h-[90vh] max-w-2xl overflow-y-auto"
          data-testid="investor-form"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#0F172A]">
            {initial?.id ? "Modifier l'investisseur" : "Ajouter un investisseur"}
          </DialogTitle>
          <DialogDescription className="sr-only">Formulaire de saisie des informations de l'investisseur.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Nom ou société">
            <Input data-testid="inv-name" value={form.name} onChange={(e) => set("name", e.target.value)} />
          </Field>
          <Field label="Email">
            <Input data-testid="inv-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="email@exemple.com" />
          </Field>
          <Field label="Téléphone">
            <Input data-testid="inv-phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+33 6 12 34 56 78" />
          </Field>
          <Field label="Pays">
            <CountryCombobox value={form.country} onChange={(v) => set("country", v)} testid="inv-country" />
          </Field>
          <Field label="Devise">
            <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
              <SelectTrigger className="h-10" data-testid="inv-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((c) => <SelectItem key={c} value={c} data-testid={`inv-currency-${c}`}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Budget minimum">
            <Input type="number" data-testid="inv-budget-min" value={form.budget_min} onChange={(e) => set("budget_min", e.target.value)} />
          </Field>
          <Field label="Budget maximum">
            <Input type="number" data-testid="inv-budget-max" value={form.budget_max} onChange={(e) => set("budget_max", e.target.value)} />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Types de biens recherchés">
              <Chips options={PROPERTY_TYPES} selected={form.types} onToggle={(v) => toggle("types", v)} testid="inv-types" />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Zones recherchées">
              <Chips options={COUNTRIES} selected={form.zones} onToggle={(v) => toggle("zones", v)} testid="inv-zones" />
            </Field>
          </div>
          <Field label="Statut du contact">
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger className="h-10" data-testid="inv-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {INVESTOR_STATUS.map((s) => <SelectItem key={s} value={s} data-testid={`inv-status-${s}`}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <div className="flex items-center justify-between rounded-xl border border-slate-200 px-4">
            <Label className="text-slate-700">NDA signé</Label>
            <Switch checked={form.nda_signed} onCheckedChange={(v) => set("nda_signed", v)} data-testid="inv-nda" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="inv-cancel">
            Annuler
          </Button>
          <Button onClick={submit} disabled={saving} className="rounded-full bg-[#0F172A] text-white hover:bg-[#1e293b]" data-testid="inv-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
